/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import { Grid, PageGridContainer } from '@coursera/cds-core';

import type { Product, ProductType } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import SkillPageContent from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillPageContent';
import type { PropsFromGetSkillsQuery } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';
import withSkillDetails from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/program-skills-objectives';

type PropsFromRouter = {
  router: InjectedRouter;
  programSlug: string;
  skillId: string;
};

type PropsFromCaller = {
  programId: string;
  program: EnterpriseProgramsV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
};

export type Props = PropsFromCaller & PropsFromRouter & PropsFromGetSkillsQuery;

/* This page displays course recommendations for a given skill outside of a skillset. */
export const ProgramSkillRecommendationsPage = ({
  programSlug,
  thirdPartyOrganization,
  programId,
  program,
  router,
  skillId,
}: Props) => {
  const onProductCardClick = (product: Product, productType: ProductType, collectionId?: string | null) => {
    if (productType === 'clip') {
      window.open(`/videos/${product.slug}`, '_blank');
    } else {
      launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
        slug: product.slug,
        productPagePath: getProductPath(product),
      });
    }
  };

  const programName = program?.metadata?.name ?? _t('My Learning Program');
  const userId = user.get().id;

  const styles = {
    page: css`
      background-color: var(--cds-color-neutral-background-primary);
    `,
    container: css`
      margin: 0 auto;
    `,
  };

  return (
    <main css={styles.container} className="rc-ProgramSkillRecommendationsPage">
      <PageGridContainer>
        <Grid item xs={12}>
          <SkillPageContent
            key={skillId}
            programId={programId}
            skillId={skillId}
            onProductCardClick={onProductCardClick}
            userId={userId}
            programSlug={programSlug}
            thirdPartyOrganization={thirdPartyOrganization}
            programName={programName}
            isAuthenticatedUser={!!userId}
          />
        </Grid>
      </PageGridContainer>
    </main>
  );
};

export const enhance = compose<Props, PropsFromCaller>(
  connectToRouter<PropsFromRouter, {}>((router) => ({
    router,
    programSlug: router.params.programSlug,
    objectiveSlug: router.params.objectiveSlug,
    skillId: router.params.skillId,
  })),
  withSkillDetails
);

const PageWithHocs = enhance(ProgramSkillRecommendationsPage);

const ProgramSkillRecommendationsPageRootContent = () => {
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  return (
    <PageWithHocs
      program={new EnterpriseProgramsV1(program)}
      programId={program?.id}
      thirdPartyOrganization={new ThirdPartyOrganizationsV1(thirdPartyOrganization)}
    />
  );
};

export default ProgramSkillRecommendationsPageRootContent;
